<template>
	<v-app>
		<main class="signup-main">
			<div class="signup-column">
				<div class="d-flex align-center mb-8">
					<img src="https://assets.liveswitch.com/shared/liveswitch-logotype.svg" alt="LiveSwitch" height="32" />
				</div>

				<div v-if="!success">
					<h1 class="text-h4 font-weight-bold mb-6">Start for free</h1>

					<v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4" density="compact">
						{{ errorMessage }}
					</v-alert>

					<v-form ref="formRef" v-model="formValid" @submit.prevent="handleSubmit">
						<EmailTextField
							v-model="email"
							label="Email"
							placeholder="email@example.com"
							:rules="emailRules"
							invalid-message="Please enter a valid email."
							autocomplete="email" />

						<v-text-field
							v-model="password"
							label="Password"
							placeholder="At least 8 characters"
							:rules="passwordRules"
							autocomplete="new-password" />

						<v-text-field v-model="orgName" label="Company name" :rules="requiredRules" autocomplete="organization" />

						<div class="name-row ga-3">
							<v-text-field v-model="firstName" label="First name" :rules="requiredRules" autocomplete="given-name" />
							<v-text-field v-model="lastName" label="Last name" :rules="requiredRules" autocomplete="family-name" />
						</div>

						<v-checkbox v-model="agreedToTerms" color="primary" density="compact" hide-details class="mt-2">
							<template #label>
								<span class="text-body-2">
									I agree to LiveSwitch's
									<a href="https://www.liveswitch.com/terms/" target="_blank" rel="noopener" @click.stop
										>Terms of Service</a
									>
									and
									<a href="https://www.liveswitch.com/privacy-policy/" target="_blank" rel="noopener" @click.stop
										>Privacy Policy</a
									>.
								</span>
							</template>
						</v-checkbox>

						<PrimaryBtn type="submit" size="large" block class="mt-4" :loading="loading" :disabled="!agreedToTerms">
							Start trial
						</PrimaryBtn>
					</v-form>

					<p class="text-caption text-medium-emphasis mt-6">
						This site is protected by reCAPTCHA and the Google
						<a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</a> and
						<a href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</a> apply.
					</p>
				</div>

				<div v-else class="text-center py-8">
					<v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
					<h2 class="text-h6 mb-2">You're all set!</h2>
					<p class="text-body-2 text-medium-emphasis">Signing you in&hellip;</p>
				</div>
			</div>
		</main>
	</v-app>
</template>

<script lang="ts" setup>
	import { onMounted, ref } from "vue";
	import { createFreeTrial } from "@/api/payments";
	import { FreeTrialRequest } from "@/types/payments/FreeTrialRequest";

	const firstName = ref("");
	const lastName = ref("");
	const email = ref("");
	const password = ref("");
	const orgName = ref("");
	const agreedToTerms = ref(false);

	const formRef = ref<any>();
	const formValid = ref(false);
	const loading = ref(false);
	const errorMessage = ref("");
	const success = ref(false);

	const requiredRules = [(value: string) => !!value?.trim() || "This field is required."];

	const emailRules = [(value: string) => !!value?.trim() || "Email is required."];

	const passwordRules = [
		(value: string) => !!value || "Password is required.",
		(value: string) =>
			/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d\S]{8,}$/.test(value) ||
			"Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.",
		(value: string) => value.length < 50 || "Password must be less than 50 characters."
	];

	onMounted(() => {
		const siteKey = (window as any).env?.VITE_RECAPTCHA_SITE_KEY;

		if (!siteKey || document.querySelector(`script[src*="recaptcha/enterprise.js"]`)) {
			return;
		}

		const script = document.createElement("script");
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	});

	async function getRecaptchaToken(): Promise<string> {
		const env = (window as any).env;
		const grecaptcha = (window as any).grecaptcha;
		const siteKey = env?.VITE_RECAPTCHA_SITE_KEY;

		if (!siteKey || !grecaptcha?.enterprise) {
			return "";
		}

		await new Promise<void>((resolve) => grecaptcha.enterprise.ready(resolve));
		return await grecaptcha.enterprise.execute(siteKey, { action: "submit" });
	}

	async function handleSubmit() {
		errorMessage.value = "";

		const validation = await formRef.value?.validate();
		if (validation && !validation.valid) {
			return;
		}

		loading.value = true;

		try {
			const recaptchaToken = await getRecaptchaToken();

			const request: FreeTrialRequest = {
				email: email.value.trim(),
				password: password.value,
				firstName: firstName.value.trim(),
				lastName: lastName.value.trim(),
				orgName: orgName.value.trim(),
				recaptchaToken: recaptchaToken
			};

			const response = await createFreeTrial(request);

			success.value = true;

			const params = new URLSearchParams({
				secret: (window as any).env.VITE_PAY_SECRET,
				accessToken: response.accessToken,
				idToken: response.idToken,
				refreshToken: response.refreshToken
			});

			// LSP-16192: preserve the shared-project context carried into /free-trial so
			// autologin can redirect the new account back to the project they were viewing.
			const incoming = new URLSearchParams(window.location.search);
			const projectId = incoming.get("projectId");
			const shareId = incoming.get("shareId");
			if (projectId && shareId) {
				params.set("projectId", projectId);
				params.set("shareId", shareId);
			}

			window.location.href = `/autologin/?${params.toString()}`;
		} catch (error: any) {
			errorMessage.value = error?.message || "Something went wrong, please try again.";
			loading.value = false;
		}
	}
</script>

<style lang="scss" scoped>
	.signup-main {
		min-height: 100vh;
		display: flex;
		justify-content: center;
		background-color: #fff;
	}

	.signup-column {
		width: 100%;
		max-width: 520px;
		padding: 56px 32px 48px;
	}

	.name-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		align-items: start;
	}

	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.signup-column {
			padding: 32px 20px;
		}
	}
</style>
